import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
} from '@mui/material';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import ScheduleIcon from '@mui/icons-material/Schedule';

const reports = [
  { name: 'Monthly Revenue Report', type: 'Financial', lastGenerated: 'Jan 15, 2026', scheduled: 'Monthly', preview: 'bar' },
  { name: 'Customer Acquisition Analysis', type: 'Marketing', lastGenerated: 'Jan 14, 2026', scheduled: 'Weekly', preview: 'line' },
  { name: 'Inventory Status Report', type: 'Operations', lastGenerated: 'Jan 15, 2026', scheduled: 'Daily', preview: 'table' },
  { name: 'Sales Performance by Region', type: 'Sales', lastGenerated: 'Jan 12, 2026', scheduled: 'Monthly', preview: 'pie' },
  { name: 'Cash Flow Forecast', type: 'Financial', lastGenerated: 'Jan 10, 2026', scheduled: 'Weekly', preview: 'area' },
  { name: 'Product Performance Dashboard', type: 'Product', lastGenerated: 'Jan 15, 2026', scheduled: 'Daily', preview: 'mixed' },
];

const typeColors: Record<string, string> = {
  Financial: '#008080',
  Marketing: '#F4A261',
  Operations: '#003366',
  Sales: '#2e7d32',
  Product: '#1976d2',
};

// Mini chart preview component
const ReportPreview: React.FC<{ type: string; color: string }> = ({ type, color }) => {
  const barHeights = [40, 65, 55, 80, 70, 90, 75];
  const linePoints = [30, 45, 35, 60, 50, 75, 65];
  const pieSegments = [35, 25, 20, 20];

  return (
    <Box
      sx={{
        height: 80,
        bgcolor: `${color}15`,
        borderRadius: '8px 8px 0 0',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        p: 1.5,
        gap: 0.5,
      }}
    >
      {type === 'bar' && barHeights.map((h, i) => (
        <Box
          key={i}
          sx={{
            width: 12,
            height: `${h}%`,
            bgcolor: color,
            borderRadius: '2px 2px 0 0',
            opacity: 0.7 + (i * 0.04),
          }}
        />
      ))}
      {type === 'line' && (
        <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
          <svg width="100%" height="100%" viewBox="0 0 100 50" preserveAspectRatio="none">
            <polyline
              fill="none"
              stroke={color}
              strokeWidth="2"
              points={linePoints.map((y, i) => `${i * 16},${50 - y * 0.6}`).join(' ')}
            />
            <polyline
              fill={`${color}30`}
              stroke="none"
              points={`0,50 ${linePoints.map((y, i) => `${i * 16},${50 - y * 0.6}`).join(' ')} 96,50`}
            />
          </svg>
        </Box>
      )}
      {type === 'table' && (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {[0, 1, 2, 3].map((i) => (
            <Box key={i} sx={{ display: 'flex', gap: 0.5 }}>
              <Box sx={{ flex: 2, height: 10, bgcolor: i === 0 ? color : `${color}40`, borderRadius: 0.5 }} />
              <Box sx={{ flex: 1, height: 10, bgcolor: i === 0 ? color : `${color}30`, borderRadius: 0.5 }} />
              <Box sx={{ flex: 1, height: 10, bgcolor: i === 0 ? color : `${color}20`, borderRadius: 0.5 }} />
            </Box>
          ))}
        </Box>
      )}
      {type === 'pie' && (
        <Box sx={{ width: 50, height: 50, position: 'relative' }}>
          <svg width="100%" height="100%" viewBox="0 0 32 32">
            <circle r="16" cx="16" cy="16" fill={`${color}20`} />
            <circle
              r="8" cx="16" cy="16"
              fill="transparent"
              stroke={color}
              strokeWidth="16"
              strokeDasharray={`${pieSegments[0]} ${100 - pieSegments[0]}`}
              transform="rotate(-90 16 16)"
            />
            <circle
              r="8" cx="16" cy="16"
              fill="transparent"
              stroke={`${color}80`}
              strokeWidth="16"
              strokeDasharray={`${pieSegments[1]} ${100 - pieSegments[1]}`}
              strokeDashoffset={`-${pieSegments[0]}`}
              transform="rotate(-90 16 16)"
            />
          </svg>
        </Box>
      )}
      {type === 'area' && (
        <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
          <svg width="100%" height="100%" viewBox="0 0 100 50" preserveAspectRatio="none">
            <polygon
              fill={`${color}40`}
              points={`0,50 ${linePoints.map((y, i) => `${i * 16},${50 - y * 0.6}`).join(' ')} 96,50`}
            />
            <polyline
              fill="none"
              stroke={color}
              strokeWidth="2"
              points={linePoints.map((y, i) => `${i * 16},${50 - y * 0.6}`).join(' ')}
            />
          </svg>
        </Box>
      )}
      {type === 'mixed' && (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
          {barHeights.slice(0, 5).map((h, i) => (
            <Box key={i} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, mb: 0.5 }} />
              <Box sx={{ width: '80%', height: `${h}%`, bgcolor: `${color}60`, borderRadius: '2px 2px 0 0' }} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const SampleReports: React.FC = () => {
  return (
    <Box>
      <Typography variant="h4" sx={{ color: 'secondary.main', fontWeight: 700, mb: 1 }}>
        Reports
      </Typography>
      <Typography sx={{ color: 'text.secondary', mb: 3 }}>
        Access and schedule automated business reports
      </Typography>

      <Grid container spacing={3}>
        {reports.map((report) => (
          <Grid item xs={12} sm={6} md={4} key={report.name}>
            <Card sx={{ border: 1, borderColor: 'divider', height: '100%', overflow: 'hidden' }}>
              {/* Preview Image */}
              <ReportPreview type={report.preview} color={typeColors[report.type]} />

              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ color: 'secondary.main', fontWeight: 600 }}>
                    {report.name}
                  </Typography>
                  <IconButton size="small" sx={{ color: 'primary.main', mt: -0.5 }}>
                    <DownloadIcon />
                  </IconButton>
                </Box>

                <Chip
                  label={report.type}
                  size="small"
                  sx={{
                    bgcolor: `${typeColors[report.type]}15`,
                    color: typeColors[report.type],
                    fontWeight: 500,
                    mb: 2,
                  }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      Last generated
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'secondary.main', fontWeight: 500 }}>
                      {report.lastGenerated}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {report.scheduled}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default SampleReports;
